/**
 * MasterDataGeneralInfoFormDialog — Create/Edit `general_info`.
 * 1:1 port of Access `frmMdQryGeneralInfoAdd`. Compact Sales Contract style.
 *
 * Single-column (Access all inputs Left=2866), field order by Top:
 *   Date → LIFFE → G2 FOB Diffs → Currency.
 * `date` là PK: DatePicker, required, disable khi edit. liffe/g2_fob_diffs
 * required, Format Standard (#,##0.00 — type=text + format on blur). currency
 * readonly default "USD" (Access Enabled=NotDefault). VBA: date/liffe/diffs trống
 * → cảnh báo; date trùng (create) → "Price in This Date is existed!" (BE raise).
 */
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdGeneralInfoMutation,
  useUpdateMdGeneralInfoMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataGeneralInfo } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, numSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataGeneralInfo | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  date: string
  liffe: string
  g2_fob_diffs: string
  currency: string
}

const EMPTY: FormState = { date: '', liffe: '', g2_fob_diffs: '', currency: 'USD' }

// Access Format='Standard' → #,##0.00. type=text để giữ dấu phẩy; format on blur.
const parseNumeric = (s: string): number | null => {
  const t = (s ?? '').replace(/,/g, '').trim()
  if (t === '') return null
  const n = Number(t)
  return Number.isNaN(n) ? null : n
}
const formatStandard = (v: string | number | null | undefined): string => {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''))
  if (Number.isNaN(n)) return String(v)
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export default function MasterDataGeneralInfoFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createGi, { isLoading: creating }] = useCreateMdGeneralInfoMutation()
  const [updateGi, { isLoading: updating }] = useUpdateMdGeneralInfoMutation()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    if (isEdit && initialValue) {
      setForm({
        date: initialValue.date ?? '',
        liffe: formatStandard(initialValue.liffe),
        g2_fob_diffs: formatStandard(initialValue.g2_fob_diffs),
        currency: initialValue.currency ?? 'USD',
      })
    } else {
      // New: auto-load hôm nay vào Date (Access default = Now).
      setForm({ ...EMPTY, date: dayjs().format('YYYY-MM-DD') })
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const dateEmpty = !form.date
  const liffeEmpty = !form.liffe.trim()
  const diffsEmpty = !form.g2_fob_diffs.trim()
  const canSave = !dateEmpty && !liffeEmpty && !diffsEmpty && !loading

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const handleSave = async () => {
    if (dateEmpty || liffeEmpty || diffsEmpty) {
      setShowErrors(true)
      return
    }
    const body = {
      liffe: parseNumeric(form.liffe),
      g2_fob_diffs: parseNumeric(form.g2_fob_diffs),
      currency: form.currency.trim() || 'USD',
    }
    try {
      if (isEdit && initialValue) {
        await updateGi({ date: initialValue.date, body }).unwrap()
        snack.success('General info updated')
      } else {
        await createGi({ date: form.date, ...body }).unwrap()
        snack.success('General info created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit General Info' : 'New General Info'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.date}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <DatePicker
              label="Date"
              format="DD-MMM-YYYY"
              disabled={isEdit}
              value={form.date ? dayjs(form.date) : null}
              onChange={(d) => set('date', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  required: true,
                  error: showErrors && dateEmpty,
                  InputLabelProps: { shrink: true },
                  inputProps: { tabIndex: isEdit ? -1 : 1 },
                  sx: isEdit ? roSx : undefined,
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="LIFFE"
              size="small"
              fullWidth
              required
              error={showErrors && liffeEmpty}
              value={form.liffe}
              onChange={(e) => set('liffe', e.target.value)}
              onBlur={() => set('liffe', formatStandard(form.liffe))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2, inputMode: 'decimal', style: { textAlign: 'right' } }}
              sx={numSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="G2 FOB Diffs"
              size="small"
              fullWidth
              required
              error={showErrors && diffsEmpty}
              value={form.g2_fob_diffs}
              onChange={(e) => set('g2_fob_diffs', e.target.value)}
              onBlur={() => set('g2_fob_diffs', formatStandard(form.g2_fob_diffs))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3, inputMode: 'decimal', style: { textAlign: 'right' } }}
              sx={numSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Currency"
              size="small"
              fullWidth
              value={form.currency}
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true }}
              inputProps={{ tabIndex: -1 }}
              sx={roSx}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
